import { fetchTokenAuth, headerAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

export const parallel = defineProduct({
  name: "Parallel",
  tagLine: "Web search and research APIs for AI",
  logo,
  tags: ["ai", "data"],
  variants: [
    productVariant({
      identifier: "parallel",
      authLabel: "API key",
      protocol: "http",
      defaultUrlPattern: () => "https://api\\.parallel\\.ai/.*",
      auth: (app) =>
        headerAuth([{ name: "x-api-key", value: app.secret.apiKey }]),
      steps: [
        {
          title: "Create an API key",
          items: [
            {
              kind: "markdown",
              content:
                "Create an API key in the [Parallel Platform](https://platform.parallel.ai/settings?tab=api-keys), then follow the [Search API quickstart](https://docs.parallel.ai/search/search-quickstart) if you need setup help.",
            },
          ],
        },
        {
          title: "Paste your API key",
          items: [
            {
              kind: "input",
              label: "API key",
              required: true,
              target: { kind: "secrets", field: "apiKey" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: () => ({
          url: "https://api.parallel.ai/v1/search",
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            search_queries: ["test"],
            mode: "turbo",
            advanced_settings: { max_results: 1 },
          }),
        }),
      },
    }),
    productVariant({
      identifier: "parallelUsage",
      authLabel: "Usage API",
      protocol: "http",
      defaultUrlPattern: () =>
        "https://api\\.parallel\\.ai/account/service/v1/.*",
      auth: (app) =>
        fetchTokenAuth({
          request: {
            url: "https://platform.parallel.ai/agent/token",
            method: "POST",
            formBody: {
              grant_type: "urn:ietf:params:oauth:grant-type:jwt-bearer",
              assertion: app.secret.identityAssertion,
            },
          },
          extract: { from: "jsonPath", paths: ["access_token"] },
          apply: { kind: "header", scheme: "Bearer" },
        }),
      steps: [
        {
          title: "Connect your Parallel organization",
          items: [
            {
              kind: "markdown",
              content:
                "An organization admin must approve access to usage data. Your Parallel organization must have Usage API early access enabled.",
            },
            {
              kind: "authorization",
              emailLabel: "Parallel admin email",
              flow: {
                kind: "userClaim",
                identityEndpoint: "https://platform.parallel.ai/agent/identity",
                tokenEndpoint: "https://platform.parallel.ai/agent/token",
                clientId: "3b",
                clientName: "3B",
                scopes: ["usage:read"],
              },
              target: { kind: "secrets", field: "identityAssertion" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: () => {
          const endDate = new Date().toISOString().slice(0, 10);
          const startDate = new Date(Date.parse(endDate) - 86_400_000)
            .toISOString()
            .slice(0, 10);
          return `https://api.parallel.ai/account/service/v1/usage?start_date=${startDate}&end_date=${endDate}`;
        },
      },
      usageHint: () =>
        "GET https://api.parallel.ai/account/service/v1/usage with start_date and end_date in YYYY-MM-DD format. Dates are UTC; end_date is exclusive. History covers the last 90 days at daily granularity, with a limit of 10 requests per minute per organization. Store results for longer history. Costs are before credits and commit drawdowns; the current day can change. api_key_id can be empty.",
    }),
  ],
});
